/**
 * Admin Context Redux Slice
 *
 * State + reducers for admin context domain.
 * Handles admin mode state and tenant selection for admin users.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { AdminContextState, TenantSummary } from '../domain/types/admin-context.types'

// Storage key for persisting selected tenant
const STORAGE_KEY = 'admin-selected-tenant'

const initialState: AdminContextState = {
  isAdmin: false,
  isLoading: false,
  selectedTenantId: null,
  selectedTenant: null,
  tenantsList: [],
}

export const adminContextSlice = createSlice({
  name: 'adminContext',
  initialState,
  reducers: {
    /**
     * Set whether the current user is an admin
     */
    setIsAdmin: (state, action: PayloadAction<boolean>) => {
      state.isAdmin = action.payload
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    /**
     * Set the list of available tenants
     */
    setTenantsList: (state, action: PayloadAction<TenantSummary[]>) => {
      state.tenantsList = action.payload
    },

    /**
     * Select a tenant (or null for "All Tenants" view)
     * Persists to localStorage for page refresh
     */
    selectTenant: (state, action: PayloadAction<TenantSummary | null>) => {
      state.selectedTenant = action.payload
      state.selectedTenantId = action.payload?.documentId ?? action.payload?.id ?? null

      // Persist to localStorage for page refresh
      if (typeof window !== 'undefined') {
        if (action.payload) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(action.payload))
        } else {
          localStorage.removeItem(STORAGE_KEY)
        }
      }
    },

    /**
     * Clear tenant selection (return to "All Tenants" view)
     */
    clearTenantSelection: (state) => {
      state.selectedTenant = null
      state.selectedTenantId = null

      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY)
      }
    },

    /**
     * Restore tenant selection from localStorage
     * Should be called on app initialization
     */
    restoreFromStorage: (state) => {
      if (typeof window === 'undefined') return

      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          const tenant = JSON.parse(stored) as TenantSummary
          state.selectedTenant = tenant
          state.selectedTenantId = tenant.documentId ?? tenant.id
        } catch {
          localStorage.removeItem(STORAGE_KEY)
        }
      }
    },

    /**
     * Reset admin context to initial state
     */
    resetAdminContext: () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY)
      }
      return initialState
    },

    /**
     * Trigger fetch of tenants list from API (handled by saga)
     */
    fetchTenantsList: (state) => {
      state.isLoading = true
    },

    /**
     * Tenants list loaded successfully
     */
    fetchTenantsListSuccess: (state, action: PayloadAction<TenantSummary[]>) => {
      state.isLoading = false
      state.tenantsList = action.payload
    },

    /**
     * Tenants list load failed
     */
    fetchTenantsListFailure: (state) => {
      state.isLoading = false
    },
  },
})

export const adminContextActions = adminContextSlice.actions
export const adminContextReducer = adminContextSlice.reducer
